import React, { useState } from "react";
import styled from "styled-components";

import Language from "@material-ui/icons/Language";
import Person from "@material-ui/icons/Person";
import ExitToApp from "@material-ui/icons/ExitToApp";
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from "swiper/react";

import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Box, Container, Popover } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Inertia } from "@inertiajs/inertia";
import Alert from "@material-ui/lab/Alert";
import { Trans } from "../../utils/i18n";
import { useLanguageSwitch } from "../../hooks/useLanguageSwitch";
import ClientSwitcher from "../ClientSwitcher";

const Nav = styled.nav`
    width: 100%;
    background-color: ${(props) => props.color || props.theme.primary};
    padding: 15px;
    font-size: 30px;
    box-shadow: 0px 7px 15px -2px rgba(255, 255, 255, 0.6);
`;

const Left = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    padding: 10px;
    flex-wrap: wrap;
`;

const Right = styled.div`
    display: flex;
    align-items: center;
`;

const Logo = styled.img`
    width: auto;
    height: 70px;
    margin-right: 10px;
`;

const CoopLogo = styled.img`
    width: 150px;
    height: auto;
`;

const FooterLogo = styled.img`
    width: auto;
    height: 32px;
    margin-bottom: 12px;

    &:last-of-type {
        margin-bottom: 0;
    }
`;

const FlashMessage = styled(Alert)`
    margin-top: 30px;
`;

const StyledLanguage = styled(Language)`
    cursor: pointer;
`;

const PopoverContent = styled.div`
    padding: 0 20px;
    border-radius: 10px;
`;

const LanguageSelect = styled.p`
    cursor: pointer;
    color: ${(props) => props.theme.primary};
`;

const SliderImage = styled.img`
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center;
    max-height: 100%;
`;

const ScrollTop = styled.button`
    background-color: #155b9c;
    width: 100%;
    padding: 12px 8px;
    border: none;
    cursor: pointer;
    color: #fff;
`;

const Footer = styled.footer`
    background-color: ${(props) => props.color || props.theme.primary};
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
`;

const FooterContent = styled.div`
    padding-top: 32px;
    padding-bottom: 24px;
`;

const SliderBulletContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 8px;
    margin-bottom: 8px;
`;

const FooterCopyright = styled.div`
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    padding: 24px;
    text-align: center;
`;

export default function DefaultLayout({ children, wrapperStyle }) {
    const {
        props: { flash, user, languages },
    } = usePage();
    const { updateLanguage } = useLanguageSwitch();

    const handleLogoutClick = async () => {
        Inertia.post(route("logout"));
    };

    const [langSwitchElement, setLangSwitchElement] = useState();

    const handleLangClick = (event) => {
        setLangSwitchElement(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setLangSwitchElement(null);
    };

    const handleLanguageChange = (language) => {
        setLangSwitchElement(null);
        updateLanguage(language);
    };

    const open = Boolean(langSwitchElement);

    const images = [
        "/images/amazon/banner1.png",
        "/images/amazon/banner2.png",
    ];

    return (
        <>
            <Nav color={"" || children.props.client.brand_color_header}>
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        <Left>
                            {children.props.client.brand_logo && (
                                <Logo
                                    src={children.props.client.brand_logo}
                                />
                            )}
                        </Left>
                        <Right>
                            <StyledLanguage
                                style={{
                                    color: children.props.client
                                        .brand_color_header_icons,
                                }}
                                onClick={handleLangClick}
                            />
                            <Popover
                                id={open ? "language-popover" : undefined}
                                open={open}
                                anchorEl={langSwitchElement}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center",
                                }}
                            >
                                <PopoverContent>
                                    {languages.map((item) => (
                                        <LanguageSelect
                                            onClick={() =>
                                                handleLanguageChange(item.code)
                                            }
                                        >
                                            { item.name }
                                        </LanguageSelect>
                                    ))}
                                </PopoverContent>
                            </Popover>
                            <ClientSwitcher />
                            {children.props.user?.id && (
                                <InertiaLink
                                    href={route("profile")}
                                    style={{ lineHeight: 0 }}
                                >
                                    <Person
                                        style={{
                                            color: children.props.client
                                                .brand_color_header_icons,
                                        }}
                                    />
                                </InertiaLink>
                            )}
                            {children.props.user?.id && (
                                <ExitToApp
                                    onClick={handleLogoutClick}
                                    style={{
                                        color: children.props.client
                                            .brand_color_header_icons,
                                        cursor: "pointer",
                                    }}
                                />
                            )}
                        </Right>
                    </Grid>
                </Container>
            </Nav>
            <header>
                <Swiper
                    autoHeight
                    loop
                    modules={[Navigation, Pagination, A11y]}
                    spaceBetween={0}
                    slidesPerView={1}
                    pagination={{
                        clickable: true,
                        el: ".swiper-bullet-container",
                        renderBullet: (index, className) => {
                            return (
                                '<span class="' + className + '">' + "</span>"
                            );
                        },
                    }}
                >
                    {images.map((src) => (
                        <SwiperSlide
                            key={src}
                            style={{ maxHeight: "500px", height: "auto" }}
                        >
                            <SliderImage src={src} alt="" />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <SliderBulletContainer className="swiper-bullet-container" />
            </header>
            <Container maxWidth="lg">
                {flash.message && (
                    <FlashMessage severity="warning">
                        {flash.message}
                    </FlashMessage>
                )}
                {flash.status && (
                    <FlashMessage severity="success">
                        {flash.status}
                    </FlashMessage>
                )}
            </Container>
            <Box paddingY={8} style={wrapperStyle}>{children}</Box>
            <ScrollTop
                onClick={() => {
                    window.scrollTo(0, 0);
                }}
            >
                Back to top
            </ScrollTop>
            <Footer color={"" || children.props.client.brand_color_header}>
                <FooterContent>
                    <Container maxWidth="md">
                        <Grid container spacing={8} justifyContent="center">
                            <Grid item xs={12} md={4}>
                                <div>
                                    Voluptate cillum proident deserunt in quis
                                    magna magna. Qui reprehenderit officia
                                    pariatur sint consequat. Amet incididunt ad
                                    in reprehenderit nisi quis. Proident labore
                                    in occaecat deserunt velit sit consequat
                                    esse voluptate velit. Eiusmod sint eiusmod
                                    ea tempor reprehenderit laboris mollit ea
                                    excepteur nulla.
                                </div>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <div>
                                    Voluptate cillum proident deserunt in quis
                                    magna magna. Qui reprehenderit officia
                                    pariatur sint consequat. Amet incididunt ad
                                    in reprehenderit nisi quis. Proident labore
                                    in occaecat deserunt velit sit consequat
                                    esse voluptate velit. Eiusmod sint eiusmod
                                    ea tempor reprehenderit laboris mollit ea
                                    excepteur nulla.
                                </div>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box
                                    borderLeft={{
                                        xs: "0px solid rgba(255,255,255,0.15)",
                                        md: "1px solid rgba(255,255,255,0.15)",
                                    }}
                                    borderTop={{
                                        xs: "1px solid rgba(255,255,255,0.15)",
                                        md: "0px solid rgba(255,255,255,0.15)",
                                    }}
                                    display="flex"
                                    height="100%"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    padding={2}
                                    style={{ backgroundColor: "inherit" }}
                                >
                                    <FooterLogo
                                        src="/images/logos/b2x.png"
                                        alt="B2X Logo"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </FooterContent>
                <FooterCopyright>
                    <Container maxWidth="md">
                        Occaecat velit dolor occaecat ea dolore velit est non
                        veniam. Reprehenderit officia consectetur enim
                        incididunt veniam sit sit officia deserunt ipsum sint
                        aliqua tempor veniam. Et id magna in ullamco mollit.
                        Consequat eiusmod commodo amet sunt. Excepteur enim elit
                        laborum enim cupidatat adipisicing id mollit labore anim
                        labore pariatur sunt.
                    </Container>
                </FooterCopyright>
            </Footer>
        </>
    );
}
